import { BaseComponent } from "./base_component"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { Node, Output } from "rete"
import { BooleanTileGrid } from "../tile_grid"
import { booleanDataSocket } from "../socket_types"
import { TextControl } from "../controls/text"
import { SelectControl } from "../controls/select"
import { createXYZ } from "ol/tilegrid"
import { MultiPolygon, Point, Polygon } from "ol/geom"
import { maskFromExtentAndShape } from "../bounding_box"
import { ProjectProperties } from "."
import {
    PlaceCandidate,
    choosePlace,
    estimatedExtentKm,
    expandPointGeometry,
    fetchPlaceBoundary,
    fetchPlaceElement,
    geometryFromOverpassElement,
    placeCandidateLabel,
    readPlaceCandidates,
    searchPlaces,
} from "./osm_place"

const PLACE_LOG = "[OpenStreetMap Place]"

function logPlace(message: string) {
    console.log(PLACE_LOG, message)
}

function elapsed(since: number): string {
    const ms = performance.now() - since
    return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`
}

function tileWidthMeters(zoom: number): number {
    const tileGrid = createXYZ()
    const tileSize = tileGrid.getTileSize(zoom)
    const pixels = Array.isArray(tileSize) ? tileSize[0] : tileSize
    return tileGrid.getResolution(zoom) * pixels
}

/** Degrees of latitude in one project cell. Nominatim thins the outline to this. */
function polygonThresholdDegrees(zoom: number, bbox?: [number, number, number, number]): number {
    const tileDegrees = tileWidthMeters(zoom) / 111_320
    if (!bbox) return Math.max(tileDegrees, 0.00001)
    const span = Math.max(bbox[2] - bbox[0], bbox[3] - bbox[1])
    // A tolerance wider than the place itself collapses a small outline to nothing.
    const featureLimit = span / 8
    return Math.max(Math.min(tileDegrees, featureLimit), 0.00001)
}

/** Skip the second simplify pass until the outline is large enough to matter. */
const COMPLEX_BOUNDARY_VERTICES = 500

function vertexCount(geometry: Polygon | MultiPolygon): number {
    return geometry.getFlatCoordinates().length / geometry.getStride()
}

/**
 * Snap a dense boundary to the project tile grid. OpenLayers quantizes
 * coordinates and drops the duplicates, which keeps the outline's topology
 * while removing detail smaller than one cell.
 */
function simplifyForTiles(geometry: Polygon | MultiPolygon, zoom: number): Polygon | MultiPolygon {
    const before = vertexCount(geometry)
    if (before < COMPLEX_BOUNDARY_VERTICES) {
        logPlace(`boundary has ${before} vertices, leaving it unchanged`)
        return geometry
    }

    const started = performance.now()
    const tolerance = tileWidthMeters(zoom)
    try {
        const simplified = geometry.simplify(tolerance)
        if (simplified instanceof Polygon || simplified instanceof MultiPolygon) {
            logPlace(`simplified ${before} → ${vertexCount(simplified)} vertices in ${elapsed(started)}`)
            return simplified
        }
    } catch (error) {
        console.warn(PLACE_LOG, "simplification failed, using the full boundary", error)
    }
    return geometry
}

type Ring = Array<[number, number]>

function outlineRings(geometry: Polygon | MultiPolygon): Ring[] {
    const polygons = geometry instanceof Polygon ? [geometry.getCoordinates()] : geometry.getCoordinates()
    const rings: Ring[] = []
    for (const polygon of polygons) {
        for (const ring of polygon) {
            if (ring.length >= 4) rings.push(ring as Ring)
        }
    }
    return rings
}

function crossingsAt(rings: Ring[], y: number): number[] {
    const xs: number[] = []
    for (const ring of rings) {
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const y0 = ring[j][1]
            const y1 = ring[i][1]
            if ((y0 > y) === (y1 > y)) continue
            const x0 = ring[j][0]
            const x1 = ring[i][0]
            xs.push(x0 + ((y - y0) / (y1 - y0)) * (x1 - x0))
        }
    }
    xs.sort((a, b) => a - b)
    return xs
}

function firstColumnAtOrAfter(
    centerX: (column: number) => number,
    first: number,
    last: number,
    target: number
): number {
    let low = first
    let high = last + 1
    while (low < high) {
        const mid = (low + high) >> 1
        if (centerX(mid) < target) low = mid + 1
        else high = mid
    }
    return low
}

function paintPlaceGeometry(
    geometry: Point | Polygon | MultiPolygon,
    grid: BooleanTileGrid,
    mask: BooleanTileGrid,
    zoom: number,
): boolean {
    const tileGrid = createXYZ()

    if (geometry instanceof Point) {
        const featureTileRange = tileGrid.getTileRangeForExtentAndZ(geometry.getExtent(), zoom)
        const x = featureTileRange.maxX
        const y = featureTileRange.minY
        if (
            x >= grid.x && x < grid.x + grid.width &&
            y >= grid.y && y < grid.y + grid.height
        ) {
            const inside = mask.get(x, y) === true
            grid.set(x, y, inside)
            return inside
        }
        return false
    }

    const featureTileRange = tileGrid.getTileRangeForExtentAndZ(geometry.getExtent(), zoom)
    const outline = simplifyForTiles(geometry, zoom)
    const rings = outlineRings(outline)
    const minX = Math.max(featureTileRange.minX, grid.x)
    const maxX = Math.min(featureTileRange.maxX, grid.x + grid.width - 1)
    const minY = Math.max(featureTileRange.minY, grid.y)
    const maxY = Math.min(featureTileRange.maxY, grid.y + grid.height - 1)
    if (rings.length === 0 || minX > maxX || minY > maxY) return false

    const centerX = (column: number) => tileGrid.getTileCoordCenter([zoom, column, minY])[0]
    const started = performance.now()
    let any = false
    let marked = 0
    for (let y = minY; y <= maxY; y++) {
        const centerY = tileGrid.getTileCoordCenter([zoom, minX, y])[1]
        const crossings = crossingsAt(rings, centerY)
        for (let i = 0; i + 1 < crossings.length; i += 2) {
            const from = firstColumnAtOrAfter(centerX, minX, maxX, crossings[i])
            const to = firstColumnAtOrAfter(centerX, minX, maxX, crossings[i + 1])
            for (let x = from; x < to; x++) {
                if (mask.get(x, y) !== true) continue
                grid.set(x, y, true)
                marked++
                any = true
            }
        }
    }
    logPlace(`filled ${marked} cells in ${elapsed(started)}`)
    if (!any) {
        const span = (maxX - minX + 1) * (maxY - minY + 1)
        if (span > 0 && span <= 8192) {
            const rescueStarted = performance.now()
            for (let y = minY; y <= maxY; y++) {
                for (let x = minX; x <= maxX; x++) {
                    if (mask.get(x, y) !== true) continue
                    if (!outline.intersectsExtent(tileGrid.getTileCoordExtent([zoom, x, y]))) continue
                    grid.set(x, y, true)
                    marked++
                    any = true
                }
            }
            if (any) logPlace(`marked ${marked} overlapping cells in ${elapsed(rescueStarted)}`)
        }
    }
    return any
}

function emptyGrid(zoom: number, extent: ProjectProperties["extent"], name: string): BooleanTileGrid {
    const tileGrid = createXYZ()
    const outputTileRange = tileGrid.getTileRangeForExtentAndZ(extent, zoom)
    const grid = new BooleanTileGrid(
        zoom,
        outputTileRange.minX,
        outputTileRange.minY,
        outputTileRange.getWidth(),
        outputTileRange.getHeight()
    )
    grid.name = name
    return grid
}

export class OSMPlaceComponent extends BaseComponent {
    outputCache: Map<string, BooleanTileGrid>
    placeSearchCache: Map<string, PlaceCandidate[]>
    placeGeometryCache: Map<string, Point | Polygon | MultiPolygon | null>
    projectProps: ProjectProperties

    constructor(projectProps: ProjectProperties) {
        super("OpenStreetMap Place")
        this.category = "Inputs"
        this.projectProps = projectProps
        this.outputCache = new Map()
        this.placeSearchCache = new Map()
        this.placeGeometryCache = new Map()
    }

    async builder(node: Node) {
        node.meta.toolTip = "Queries Nominatim for a place name then retrieves the OSM geometry for that place. If the place is a point, Nominatim's estimated extent is used instead."
        node.meta.toolTipLink = "https://wiki.openstreetmap.org/wiki/Key:place"

        node.addOutput(new Output("out", "Output", booleanDataSocket))
        const placeSearch = new TextControl(this.editor, "placeName", "Place", "100%")
        placeSearch.placement = "top"
        node.addControl(placeSearch)
        node.addControl(
            new SelectControl(
                this.editor,
                "placeMatch",
                () => readPlaceCandidates(node.data.placeCandidates).map(candidate => {
                    const option = placeCandidateLabel(candidate)
                    return {
                        id: candidate.osmId,
                        name: option.name,
                        badge: option.badge,
                    }
                }),
                () => {},
                "Match"
            )
        )
    }

    async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
        const editorNode = this.editor?.nodes.find(n => n.id === node.id)
        if (editorNode === undefined) return

        const publish = (grid: BooleanTileGrid) => {
            editorNode.meta.output = outputs["out"] = grid
            editorNode.update()
        }

        try {
            const placeName = typeof node.data.placeName === "string" ? node.data.placeName.trim() : ""
            if (placeName === "") {
                publish(emptyGrid(this.projectProps.zoom, this.projectProps.extent, "Enter a place name"))
                return
            }

            const mask = await maskFromExtentAndShape(this.projectProps)
            const tileGrid = createXYZ()
            const outputTileRange = tileGrid.getTileRangeForExtentAndZ(
                this.projectProps.extent,
                this.projectProps.zoom
            )
            const place = await this.placeMask(editorNode, placeName, node.data.placeMatch, mask, outputTileRange)
            if (!place) {
                publish(emptyGrid(this.projectProps.zoom, this.projectProps.extent, `No place found for ${placeName}`))
                return
            }
            place.grid.name = place.label
            publish(place.grid)
        } catch (error) {
            console.error("Error in OSMPlaceComponent worker:", error)
            publish(emptyGrid(this.projectProps.zoom, this.projectProps.extent, "Error loading place"))
        }
    }

    private async placeMask(
        editorNode: Node,
        placeName: string,
        selectedId: unknown,
        mask: BooleanTileGrid,
        outputTileRange: { minX: number, minY: number, getWidth(): number, getHeight(): number }
    ): Promise<{ grid: BooleanTileGrid, label: string } | null> {
        const started = performance.now()
        // The suffix drops matches cached before point places carried an estimated extent.
        const searchKey = `${placeName.trim().toLowerCase()}|area`
        let candidates = this.placeSearchCache.get(searchKey)
        if (!candidates) {
            logPlace(`searching for "${placeName}"`)
            const searchStarted = performance.now()
            candidates = await searchPlaces(placeName)
            this.placeSearchCache.set(searchKey, candidates)
            logPlace(`search returned ${candidates.length} match${candidates.length === 1 ? "" : "es"} in ${elapsed(searchStarted)}`)
        } else {
            logPlace(`using ${candidates.length} cached match${candidates.length === 1 ? "" : "es"} for "${placeName}"`)
        }
        editorNode.data.placeCandidates = candidates
        this.refreshPlaceMatch(editorNode)

        const chosen = choosePlace(candidates, editorNode.data.placeMatch ?? selectedId)
        if (!chosen) {
            logPlace(`no match for "${placeName}"`)
            return null
        }
        logPlace(`using ${chosen.kind}: ${chosen.shortLabel || chosen.displayName} (${chosen.osmType} ${chosen.osmId})`)

        if (String(editorNode.data.placeMatch ?? "") !== String(chosen.osmId)) {
            editorNode.controls.get("placeMatch")?.putData("placeMatch", String(chosen.osmId))
            this.refreshPlaceMatch(editorNode)
        }

        const toleranceMeters = Math.round(tileWidthMeters(this.projectProps.zoom))
        const geometryKey = `${chosen.osmType}/${chosen.osmId}@${toleranceMeters}|extent`
        const gridKey = `place:${geometryKey}`
        const cachedGrid = this.outputCache.get(gridKey)
        if (cachedGrid) {
            logPlace(`using the cached grid (${elapsed(started)})`)
            return { grid: cachedGrid, label: cachedGrid.name || chosen.displayName }
        }

        let geometry = this.placeGeometryCache.get(geometryKey)
        if (geometry === undefined) {
            const threshold = polygonThresholdDegrees(this.projectProps.zoom, chosen.bbox)
            logPlace(`loading a boundary simplified to about ${toleranceMeters} m`)
            const boundaryStarted = performance.now()
            try {
                geometry = await fetchPlaceBoundary(chosen, threshold)
            } catch (error) {
                console.warn(PLACE_LOG, "simplified boundary request failed", error)
                geometry = null
            }
            if (geometry) {
                const vertices = geometry instanceof Point ? 1 : vertexCount(geometry)
                logPlace(`simplified boundary loaded in ${elapsed(boundaryStarted)} · ${vertices} vertices`)
            } else {
                logPlace(`no simplified boundary after ${elapsed(boundaryStarted)}, loading the full outline`)
                const fullStarted = performance.now()
                const element = await fetchPlaceElement(chosen)
                geometry = element ? geometryFromOverpassElement(element) : null
                const vertices = !geometry || geometry instanceof Point ? 0 : vertexCount(geometry)
                logPlace(`full outline loaded in ${elapsed(fullStarted)} · ${vertices} vertices`)
            }
            if (geometry instanceof Point && chosen.bbox) {
                const area = expandPointGeometry(geometry, chosen.bbox)
                if (area instanceof Polygon) {
                    const km = estimatedExtentKm(chosen.bbox)
                    logPlace(`OpenStreetMap only has a point, using the estimated ${km.toFixed(1)} km extent`)
                    geometry = area
                }
            }
            this.placeGeometryCache.set(geometryKey, geometry)
        } else {
            logPlace("using the cached boundary")
        }

        const grid = new BooleanTileGrid(
            this.projectProps.zoom,
            outputTileRange.minX,
            outputTileRange.minY,
            outputTileRange.getWidth(),
            outputTileRange.getHeight()
        )
        const covered = geometry ? paintPlaceGeometry(geometry, grid, mask, this.projectProps.zoom) : false
        const label = !geometry
            ? `${chosen.displayName} (no boundary)`
            : covered
                ? chosen.displayName
                : `${chosen.displayName} (no cells in the project area)`
        grid.name = label
        this.outputCache.set(gridKey, grid)
        logPlace(`finished in ${elapsed(started)}`)
        return { grid, label }
    }

    private refreshPlaceMatch(editorNode: Node) {
        const matchControl: any = editorNode.controls.get("placeMatch")
        matchControl?.update?.()
    }
}
